import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Check,
    Heart,
    ArrowLeft,
    Crown,
    Sparkles,
    Clock,
    X,
    Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlanSelection() {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [waitlistName, setWaitlistName] = useState('');
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [waitlistSuccess, setWaitlistSuccess] = useState(false);
    const [waitlistError, setWaitlistError] = useState('');

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        setWaitlistLoading(true);
        setWaitlistError('');
        
        try {
            const response = await axios.post(`${API}/api/waitlist`, {
                name: waitlistName,
                email: waitlistEmail
            });
            setWaitlistSuccess(true);
            if (response.data.already_registered) {
                setWaitlistError("You're already on the waitlist!");
            }
        } catch (err) {
            setWaitlistError(err.response?.data?.detail || 'Failed to join waitlist. Please try again.');
        } finally {
            setWaitlistLoading(false);
        }
    };

    const plans = [
        {
            id: 'essential',
            name: 'Essential',
            price: '$9',
            period: 'per month',
            yearlyPrice: '$99/year',
            yearlySavings: 'Save $9',
            description: 'Complete tracking for health management',
            features: [
                'Symptom tracking & patterns',
                'Sleep & food logging',
                'Weather tracking',
                'Flare journal',
                'Knowledge base access',
                'Community forum',
                'Voice journaling',
                'AI health chat (limited)',
                'Cycle tracker'
            ],
            color: 'from-teal-500 to-emerald-500',
            popular: false
        },
        {
            id: 'premium',
            name: 'Premium',
            price: '$19',
            period: 'per month',
            yearlyPrice: '$199/year',
            yearlySavings: 'Save $29',
            description: 'Complete toolkit for optimal health outcomes',
            features: [
                'Everything in Essential',
                'Unlimited AI health chat',
                'Smart correlations & insights',
                'Flare predictions',
                'Medical timeline',
                'Lab results tracking',
                'Supplement & medication tracker',
                'Medication reminders',
                'Appointment management',
                'PDF export & reports',
                'Weekly email summaries (Coming Soon)',
                'Priority support'
            ],
            color: 'from-indigo-500 to-violet-600',
            popular: true
        },
        {
            id: 'professional',
            name: 'Professional',
            price: 'Coming Soon',
            period: '',
            yearlyPrice: '',
            yearlySavings: '',
            description: 'Advanced features for comprehensive care',
            limitedSpots: true,
            features: [
                'Everything in Premium',
                'Async Messaging with Specialist',
                'Lab Interpretation (Educational)',
                'Personalized Educational Summaries',
                'Care Pathway Education',
                'Priority Access to New Tools',
                'Direct Support Channel'
            ],
            color: 'from-amber-500 to-orange-500',
            popular: false,
            comingSoon: true
        }
    ];

    const handleContinue = async () => {
        if (!selectedPlan) return;
        
        setIsProcessing(true);
        try {
            const response = await axios.post(`${API}/api/payments/create-checkout`, {
                plan: selectedPlan,
                billing_period: billingPeriod,
                origin_url: window.location.origin
            });
            
            // Redirect to Stripe checkout
            window.location.href = response.data.checkout_url;
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('Failed to start checkout. Please try again.');
            setIsProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/">
                            <Button variant="ghost" className="text-slate-300 hover:text-white gap-2">
                                <ArrowLeft className="w-4 h-4" />
                                Back
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <Badge className="mb-4 bg-indigo-500/20 text-indigo-300 border-indigo-500/30 px-4 py-1.5">
                            <Sparkles className="w-4 h-4 mr-2" />
                            Choose Your Plan
                        </Badge>
                        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Select Your Health Journey Plan
                        </h1>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
                            Choose the plan that best fits your needs. You can upgrade or downgrade anytime.
                        </p>
                        
                        {/* Billing Period Toggle */}
                        <div className="inline-flex items-center gap-4 bg-slate-800/50 rounded-full p-1.5">
                            <button
                                onClick={() => setBillingPeriod('monthly')}
                                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                                    billingPeriod === 'monthly'
                                        ? 'bg-indigo-500 text-white'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingPeriod('annual')}
                                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                                    billingPeriod === 'annual'
                                        ? 'bg-indigo-500 text-white'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                Annual
                                <span className="ml-2 text-xs text-emerald-400">Save up to 17%</span>
                            </button>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                        {plans.map((plan) => (
                            <Card 
                                key={plan.id}
                                className={`relative transition-all duration-300 ${
                                    plan.comingSoon 
                                        ? 'bg-slate-800/30 border-slate-700/50 opacity-80' 
                                        : selectedPlan === plan.id 
                                            ? 'bg-slate-800 border-2 border-indigo-500 ring-4 ring-indigo-500/20 cursor-pointer' 
                                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 cursor-pointer'
                                } ${plan.popular ? 'md:-mt-4 md:mb-4' : ''}`}
                                onClick={() => !plan.comingSoon && setSelectedPlan(plan.id)}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-indigo-500 text-white gap-1">
                                            <Crown className="w-3 h-3" />
                                            Most Popular
                                        </Badge>
                                    </div>
                                )}
                                {plan.comingSoon && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-2">
                                        <Badge className="bg-amber-500 text-white gap-1">
                                            <Clock className="w-3 h-3" />
                                            Coming Soon
                                        </Badge>
                                        {plan.limitedSpots && (
                                            <Badge className="bg-rose-500 text-white">
                                                Limited Spots
                                            </Badge>
                                        )}
                                    </div>
                                )}
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="font-semibold text-xl text-white">{plan.name}</h3>
                                            <p className="text-sm text-slate-400">{plan.description}</p>
                                        </div>
                                        {!plan.comingSoon && (
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                                selectedPlan === plan.id 
                                                    ? 'border-indigo-500 bg-indigo-500' 
                                                    : 'border-slate-600'
                                            }`}>
                                                {selectedPlan === plan.id && (
                                                    <Check className="w-4 h-4 text-white" />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="mb-2">
                                        <span className={`font-bold text-white ${plan.comingSoon ? 'text-2xl' : 'text-4xl'}`}>{plan.price}</span>
                                        {plan.period && <span className="text-slate-400">/{plan.period}</span>}
                                    </div>
                                    {plan.yearlyPrice && (
                                        <p className="text-sm text-emerald-400 mb-6">
                                            or {plan.yearlyPrice} <span className="text-emerald-300">({plan.yearlySavings})</span>
                                        </p>
                                    )}
                                    {plan.comingSoon && <div className="mb-6" />}

                                    <ul className="space-y-2.5">
                                        {plan.features.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-2 text-sm">
                                                <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.comingSoon ? 'text-slate-500' : 'text-emerald-400'}`} />
                                                <span className={plan.comingSoon ? 'text-slate-500' : 'text-slate-300'}>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    {plan.comingSoon && (
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowWaitlistModal(true);
                                            }}
                                            className="w-full mt-6 bg-amber-600 hover:bg-amber-700 text-white"
                                        >
                                            Join Waitlist
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <div className="text-center">
                        <Button
                            size="lg"
                            onClick={handleContinue}
                            disabled={!selectedPlan || isProcessing}
                            className={`px-12 py-6 text-lg gap-2 ${
                                selectedPlan && !isProcessing
                                    ? 'bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700' 
                                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            }`}
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                `Continue to Payment`
                            )}
                        </Button>
                        <p className="mt-4 text-sm text-slate-500">
                            Already have an account?{' '}
                            <Link to="/login" className="text-indigo-400 hover:text-indigo-300">
                                Log in
                            </Link>
                        </p>
                    </div>

                    <div className="mt-12 text-center">
                        <p className="text-sm text-slate-500">
                            Cancel anytime. Your access continues until the end of your billing period.
                        </p>
                    </div>
                </div>
            </div>

            {/* Waitlist Modal */}
            {showWaitlistModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <Card className="w-full max-w-md bg-slate-900 border-slate-700">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-white">Join the Waitlist</h3>
                                <button
                                    onClick={() => {
                                        setShowWaitlistModal(false);
                                        setWaitlistSuccess(false);
                                        setWaitlistError('');
                                        setWaitlistName('');
                                        setWaitlistEmail('');
                                    }}
                                    className="text-slate-400 hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            
                            {waitlistSuccess ? (
                                <div className="text-center py-4">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                        <Check className="w-8 h-8 text-emerald-400" />
                                    </div>
                                    <h4 className="text-lg font-semibold text-white mb-2">You're on the list!</h4>
                                    <p className="text-slate-400">
                                        We'll notify you as soon as the Professional tier is available.
                                    </p>
                                    <Button
                                        onClick={() => {
                                            setShowWaitlistModal(false);
                                            setWaitlistSuccess(false);
                                        }}
                                        className="mt-6 bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        Got it!
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleWaitlistSubmit}>
                                    <p className="text-slate-400 mb-6">
                                        Be the first to know when our Professional tier launches with specialist messaging, 
                                        lab interpretation, and more.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="waitlist-name" className="text-slate-300">Name</Label>
                                            <Input
                                                id="waitlist-name"
                                                type="text"
                                                value={waitlistName}
                                                onChange={(e) => setWaitlistName(e.target.value)}
                                                placeholder="Your name"
                                                required
                                                className="mt-1 bg-slate-800 border-slate-700 text-white"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="waitlist-email" className="text-slate-300">Email</Label>
                                            <Input
                                                id="waitlist-email"
                                                type="email"
                                                value={waitlistEmail}
                                                onChange={(e) => setWaitlistEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                required
                                                className="mt-1 bg-slate-800 border-slate-700 text-white"
                                            />
                                        </div>
                                    </div>
                                    
                                    {waitlistError && (
                                        <p className="mt-4 text-sm text-amber-400">{waitlistError}</p>
                                    )}
                                    
                                    <Button
                                        type="submit"
                                        disabled={waitlistLoading}
                                        className="w-full mt-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                                    >
                                        {waitlistLoading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Joining...
                                            </>
                                        ) : (
                                            'Join Waitlist'
                                        )}
                                    </Button>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
